import { useCallback } from "react";
import { useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

import { isValid, parse } from "date-fns";

import DateStringFormField from "components/date-string-form-field/date-string-form-field.component";
import { DATE_MASK } from "components/date-string-form-field/date-string-form-field.component";
import Dialog from "components/dialog";

type BulkUpdateDueDateModalProps = {
  isOpen: boolean;
  selectedCount: number;
  onClose: () => void;
  onSave: (dueDate: string | null) => void;
  isLoading?: boolean;
};

export default function BulkUpdateDueDateModal({
  isOpen,
  selectedCount,
  onClose,
  onSave,
  isLoading = false
}: BulkUpdateDueDateModalProps) {
  const { control, handleSubmit, reset } = useForm<{ due_at: string | null }>({
    defaultValues: {
      due_at: null
    }
  });

  const onSubmit = useCallback(
    (data: { due_at: string | null }) => {
      let formattedDate: string | null = null;
      if (data.due_at) {
        const parsedDate = parse(data.due_at, DATE_MASK, new Date());
        if (isValid(parsedDate)) {
          formattedDate = parsedDate.toISOString();
        }
      }
      onSave(formattedDate);
      reset();
    },
    [onSave, reset]
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <Dialog
      title={`Update Due Date for ${selectedCount} task${selectedCount !== 1 ? "s" : ""}`}
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} sx={{ p: 2 }}>
          <DateStringFormField
            name="due_at"
            control={control}
            label="Due Date"
            required={false}
            sx={{ width: "100%" }}
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}
